/* eslint-disable prettier/prettier */
import React, { useState } from 'react';
import styles from '../../../styles/components/pageComponents/program/EditProgramLearnings.module.css';

interface EditProgramLearningsProps {
	onCancel: () => void;
	onSave: (data: { programIncludes: string[]; takeaways: string[] }) => void;
	initialIncludes?: string[];
	initialTakeaways?: string[];
}

// Reusable SVG Components
const TrashIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="20"
		height="22"
		viewBox="0 0 20 22"
		fill="none"
	>
		<path
			d="M7.75 9.75V15.75M11.75 9.75V15.75M16.75 4.75V18.75C16.75 19.2804 16.5393 19.7891 16.1642 20.1642C15.7891 20.5393 15.2804 20.75 14.75 20.75H4.75C4.21957 20.75 3.71086 20.5393 3.33579 20.1642C2.96071 19.7891 2.75 19.2804 2.75 18.75V4.75M0.75 4.75H18.75M5.75 4.75V2.75C5.75 2.21957 5.96071 1.71086 6.33579 1.33579C6.71086 0.960714 7.21957 0.75 7.75 0.75H11.75C12.2804 0.75 12.7891 0.960714 13.1642 1.33579C13.5393 1.71086 13.75 2.21957 13.75 2.75V4.75"
			stroke="#6B7280"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const CheckIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 27 27"
		fill="none"
	>
		<path
			d="M25.4983 10.7542C26.0692 13.5559 25.6623 16.4685 24.3456 19.0065C23.0289 21.5444 20.8819 23.5543 18.2627 24.7009C15.6435 25.8475 12.7103 26.0615 9.95244 25.3072C7.19453 24.5529 4.77856 22.876 3.10742 20.556C1.43627 18.236 0.610975 15.4133 0.769153 12.5585C0.92733 9.70367 2.05942 6.98937 3.97663 4.86822C5.89385 2.74707 8.48029 1.3473 11.3046 0.902326C14.129 0.457355 17.0205 0.994084 19.4971 2.42301M9.49706 12.0042L13.2471 15.7542L25.747 3.25426"
			stroke="#147B58"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const EditProgramLearnings: React.FC<EditProgramLearningsProps> = ({
	onCancel,
	onSave,
	initialIncludes = ['', '', '', '', ''],
	initialTakeaways = ['', '', '', '', ''],
}) => {
	const [programIncludes, setProgramIncludes] =
		useState<string[]>(initialIncludes);
	const [takeaways, setTakeaways] = useState<string[]>(initialTakeaways);

	// Handlers
	const updateArrayItem = (
		setter: React.Dispatch<React.SetStateAction<string[]>>,
		index: number,
		value: string
	) => {
		setter(prev => {
			const newArr = [...prev];
			newArr[index] = value;
			return newArr;
		});
	};
	const addArrayItem = (
		setter: React.Dispatch<React.SetStateAction<string[]>>
	) => setter(prev => [...prev, '']);
	const removeArrayItem = (
		setter: React.Dispatch<React.SetStateAction<string[]>>,
		index: number
	) => setter(prev => prev.filter((_, i) => i !== index));

	const handleSaveChanges = () => {
		// Filter out empty strings before saving if desired
		onSave({
			programIncludes: programIncludes.filter(Boolean),
			takeaways: takeaways.filter(Boolean),
		});
	};

	return (
		<div className={styles.modalOverlay}>
			<div className={styles.modalContainer}>
				{/* Header */}
				<div className={styles.header}>
					<h2 className={styles.title}>
						Update Program Details - Learnings & Takeaway
					</h2>
					<div className={styles.actionButtons}>
						<button className={styles.btnCancel} onClick={onCancel}>
							Cancel
						</button>
						<button
							className={styles.btnSave}
							onClick={handleSaveChanges}
						>
							Save Changes
						</button>
					</div>
				</div>

				{/* Body Content */}
				<div className={styles.formBody}>
					{/* Program Includes Column */}
					<div className={styles.column}>
						<div className={styles.columnHeader}>
							<h3>Program Includes:</h3>
							<div className={styles.divider}></div>
						</div>
						<div className={styles.listContainer}>
							{programIncludes.map((item, idx) => (
								<div
									key={`include-${idx}`}
									className={styles.inputRow}
								>
									<input
										type="text"
										className={styles.inputField}
										placeholder="Program Detail"
										value={item}
										onChange={e =>
											updateArrayItem(
												setProgramIncludes,
												idx,
												e.target.value
											)
										}
									/>
									<button
										className={styles.iconBtn}
										onClick={() =>
											removeArrayItem(
												setProgramIncludes,
												idx
											)
										}
										aria-label="Delete"
									>
										<TrashIcon />
									</button>
								</div>
							))}
						</div>
						<button
							className={styles.addBtn}
							onClick={() => addArrayItem(setProgramIncludes)}
						>
							Add New Row +
						</button>
					</div>

					{/* Learnings & Takeaway Column */}
					<div
						className={`${styles.column} ${styles.grayBackground}`}
					>
						<div className={styles.columnHeader}>
							<h3>Learnings & Takeaway</h3>
							<div className={styles.divider}></div>
						</div>
						<div className={styles.listContainer}>
							{takeaways.map((item, idx) => (
								<div
									key={`takeaway-${idx}`}
									className={styles.inputRow}
								>
									<span className={styles.checkIconWrapper}>
										<CheckIcon />
									</span>
									<input
										type="text"
										className={styles.inputField}
										placeholder="Key Takeaway"
										value={item}
										onChange={e =>
											updateArrayItem(
												setTakeaways,
												idx,
												e.target.value
											)
										}
									/>
									<button
										className={styles.iconBtn}
										onClick={() =>
											removeArrayItem(setTakeaways, idx)
										}
										aria-label="Delete"
									>
										<TrashIcon />
									</button>
								</div>
							))}
						</div>
						<button
							className={styles.addBtn}
							onClick={() => addArrayItem(setTakeaways)}
						>
							Add New Row +
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default EditProgramLearnings;
